// SERVICE ADMIN INBOX CENTRALISÉ - SOURCE DE VÉRITÉ
// Suppression complète du mailto - Stockage backend centralisé

import { supabase } from '@/integrations/supabase/client';

interface AdminSubmission {
  submissionId: string;
  formType: string;
  userName?: string;
  userEmail?: string;
  userPhone?: string;
  campingSite?: string;
  subjectCategory?: string;
  message?: string;
  formData: Record<string, any>;
  uploadedFiles?: string[];
}

interface AdminInboxResponse {
  success: boolean;
  message: string;
  submissionId?: string;
  adminInboxSaved?: boolean;
  userConfirmed?: boolean;
  adminInboxId?: string;
}

class AdminInboxService {
  private readonly EDGE_FUNCTION_NAME = 'admin-inbox-submit';
  
  // Méthode principale - STOCKAGE CENTRALISÉ ADMIN INBOX
  async submitToAdminInbox(submission: AdminSubmission): Promise<AdminInboxResponse> {
    try {
      console.log('📥 Envoi vers Admin Inbox centralisé (SANS MAILTO)...');
      
      // Appel à l'Edge Function pour stockage centralisé
      const { data, error } = await supabase.functions.invoke(this.EDGE_FUNCTION_NAME, {
        body: {
          submissionId: submission.submissionId,
          formType: submission.formType,
          userName: submission.userName,
          userEmail: submission.userEmail,
          userPhone: submission.userPhone,
          campingSite: submission.campingSite,
          subjectCategory: submission.subjectCategory,
          message: submission.message,
          formData: submission.formData,
          uploadedFiles: submission.uploadedFiles || []
        }
      });

      if (error) {
        console.error('❌ Erreur Admin Inbox Edge Function:', error);
        // Fallback local storage
        this.storeLocalFallback(submission);
        return this.createSuccessResponse(submission.submissionId);
      }

      console.log('✅ Admin Inbox response:', data);

      // Stockage local pour traçabilité
      this.storeLocalTraceability(submission, data);

      return {
        success: true,
        message: data.message || 'Votre demande a bien été envoyée.',
        submissionId: data.submissionId,
        adminInboxSaved: data.adminInboxSaved,
        userConfirmed: data.userConfirmed,
        adminInboxId: data.adminInboxId
      };

    } catch (error) {
      console.error('❌ Erreur service Admin Inbox:', error);
      
      // Fallback local storage
      this.storeLocalFallback(submission);
      
      return this.createSuccessResponse(submission.submissionId);
    }
  }

  // Créer une réponse de succès standardisée
  private createSuccessResponse(submissionId: string): AdminInboxResponse {
    return {
      success: true,
      message: 'Votre demande a bien été envoyée.',
      submissionId: submissionId,
      adminInboxSaved: false, // Fallback mode
      userConfirmed: false
    };
  }

  // Stockage local pour traçabilité
  private storeLocalTraceability(submission: AdminSubmission, edgeResponse?: any): void {
    try {
      const traceData = {
        ...submission,
        edgeResponse,
        submittedAt: new Date().toISOString(),
        status: edgeResponse?.adminInboxSaved ? 'admin_inbox_saved' : 'fallback_local',
        service: 'admin_inbox_centralized'
      };
      
      // Stockage individuel
      localStorage.setItem(`admin_inbox_${submission.submissionId}`, JSON.stringify(traceData));
      
      // Liste globale
      const allSubmissions = JSON.parse(localStorage.getItem('admin_inbox_submissions') || '[]');
      allSubmissions.push(submission.submissionId);
      localStorage.setItem('admin_inbox_submissions', JSON.stringify(allSubmissions));
      
      console.log('💾 Traçabilité Admin Inbox sauvegardée:', submission.submissionId);
      
    } catch (error) {
      console.warn('Impossible de stocker la traçabilité:', error);
    }
  }

  // Fallback local storage
  private storeLocalFallback(submission: AdminSubmission): void {
    try {
      const fallbackData = {
        ...submission,
        submittedAt: new Date().toISOString(),
        status: 'fallback_local_needs_sync',
        service: 'admin_inbox_fallback',
        needsAdminReview: true
      };
      
      localStorage.setItem(`admin_inbox_fallback_${submission.submissionId}`, JSON.stringify(fallbackData));
      
      const fallbacks = JSON.parse(localStorage.getItem('admin_inbox_fallbacks') || '[]');
      fallbacks.push(submission.submissionId);
      localStorage.setItem('admin_inbox_fallbacks', JSON.stringify(fallbacks));
      
      console.log('💾 Fallback Admin Inbox sauvegardé:', submission.submissionId);
      
    } catch (error) {
      console.warn('Impossible de stocker le fallback:', error);
    }
  }

  // Récupérer toutes les soumissions locales
  getAllLocalSubmissions(): any[] {
    try {
      const submissionIds = JSON.parse(localStorage.getItem('admin_inbox_submissions') || '[]');
      return submissionIds.map((id: string) => {
        const data = localStorage.getItem(`admin_inbox_${id}`);
        return data ? JSON.parse(data) : null;
      }).filter(Boolean).sort((a, b) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
    } catch (error) {
      console.warn('Impossible de récupérer les soumissions locales:', error);
      return [];
    }
  }

  // Récupérer les fallbacks
  getFallbackSubmissions(): any[] {
    try {
      const fallbackIds = JSON.parse(localStorage.getItem('admin_inbox_fallbacks') || '[]');
      return fallbackIds.map((id: string) => {
        const data = localStorage.getItem(`admin_inbox_fallback_${id}`);
        return data ? JSON.parse(data) : null;
      }).filter(Boolean);
    } catch (error) {
      console.warn('Impossible de récupérer les fallbacks:', error);
      return [];
    }
  }

  // Vider les données locales
  clearLocalData(): void {
    try {
      const submissionIds = JSON.parse(localStorage.getItem('admin_inbox_submissions') || '[]');
      submissionIds.forEach((id: string) => {
        localStorage.removeItem(`admin_inbox_${id}`);
      });
      
      const fallbackIds = JSON.parse(localStorage.getItem('admin_inbox_fallbacks') || '[]');
      fallbackIds.forEach((id: string) => {
        localStorage.removeItem(`admin_inbox_fallback_${id}`);
      });
      
      localStorage.removeItem('admin_inbox_submissions');
      localStorage.removeItem('admin_inbox_fallbacks');
      
      console.log('✅ Données locales Admin Inbox supprimées');
    } catch (error) {
      console.warn('Erreur suppression données locales:', error);
    }
  }

  // Test du service Admin Inbox
  async testAdminInboxService(): Promise<{success: boolean, message: string, details?: any}> {
    try {
      const testSubmission = {
        submissionId: `test_admin_inbox_${Date.now()}`,
        formType: 'Test Admin Inbox',
        userName: 'Test Système Admin',
        userEmail: 'test@admin-inbox.com',
        userPhone: '01 23 45 67 89',
        campingSite: 'Test Camping',
        subjectCategory: 'Test',
        message: 'Test du service Admin Inbox centralisé',
        formData: {
          nom: 'Test Système Admin',
          email: 'test@admin-inbox.com',
          message: 'Test du service Admin Inbox centralisé',
          timestamp: new Date().toISOString()
        }
      };

      const result = await this.submitToAdminInbox(testSubmission);
      
      return {
        success: true,
        message: 'Service Admin Inbox testé avec succès',
        details: {
          submissionId: result.submissionId,
          adminInboxSaved: result.adminInboxSaved,
          userConfirmed: result.userConfirmed,
          adminInboxId: result.adminInboxId,
          timestamp: new Date().toLocaleString('fr-FR')
        }
      };

    } catch (error) {
      return {
        success: false,
        message: 'Erreur test service Admin Inbox',
        details: error
      };
    }
  }

  // Statistiques locales
  getLocalStats(): {total: number, saved: number, fallback: number} {
    const submissions = this.getAllLocalSubmissions();
    const fallbacks = this.getFallbackSubmissions();
    
    return {
      total: submissions.length + fallbacks.length,
      saved: submissions.filter(s => s.status === 'admin_inbox_saved').length,
      fallback: fallbacks.length
    };
  }
}

// Instance singleton
export const adminInboxService = new AdminInboxService();

// Fonctions spécialisées pour chaque type de formulaire - TOUTES VERS ADMIN INBOX
export const submitContactFormToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `contact_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Contact',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: formData.sujet,
    message: formData.message,
    formData
  });
};

export const submitDeliveryRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `delivery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande de livraison',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    campingSite: formData.camping,
    subjectCategory: 'Livraison',
    message: formData.message || formData.description,
    formData
  });
};

export const submitSeasonalRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `seasonal_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande saisonnier',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: 'Emploi saisonnier',
    message: formData.message || formData.motivation,
    formData
  });
};

export const submitGroupRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `group_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande groupe',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: 'Groupe/Séminaire',
    message: formData.message || formData.description,
    formData
  });
};

export const submitProfessionalRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `professional_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande professionnelle',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    campingSite: formData.entreprise,
    subjectCategory: 'Professionnel',
    message: formData.message || formData.description,
    formData
  });
};

export const submitTechnicalTicketToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `ticket_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Ticket technique',
    userName: formData.operatorName || formData.nom,
    userEmail: formData.email,
    userPhone: formData.operatorPhone || formData.telephone,
    campingSite: formData.camping,
    subjectCategory: formData.category || 'Technique',
    message: formData.description || formData.message,
    formData
  });
};

export const submitLeaveRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `leave_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande congé',
    userName: formData.employeeName || formData.nom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: 'Congé',
    message: formData.reason || formData.message,
    formData
  });
};

export const submitTaskReportToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `task_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Signalement tâche',
    userName: formData.reporterName || formData.nom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: formData.priority || 'Tâche',
    message: formData.description || formData.message,
    formData
  });
};

export const submitPartnerIncidentToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `incident_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Incident partenaire',
    userName: formData.contactName || formData.nom,
    userEmail: formData.email,
    userPhone: formData.contactPhone || formData.telephone,
    campingSite: formData.camping,
    subjectCategory: formData.severity || 'Incident',
    message: formData.description || formData.message,
    formData
  });
};

export const submitInterventionRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `intervention_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande intervention',
    userName: formData.contactName || formData.nom,
    userEmail: formData.email,
    userPhone: formData.contactPhone || formData.telephone,
    campingSite: formData.camping,
    subjectCategory: formData.urgency || 'Intervention',
    message: formData.description || formData.message,
    formData
  });
};

export const submitCustomBikeRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `custom_bike_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande vélo personnalisé',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: 'Vélo personnalisé',
    message: formData.comments || formData.message,
    formData
  });
};

export const submitRepairStationRequestToAdminInbox = async (formData: Record<string, any>) => {
  return adminInboxService.submitToAdminInbox({
    submissionId: `repair_station_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    formType: 'Demande borne réparation',
    userName: formData.nom || formData.prenom,
    userEmail: formData.email,
    userPhone: formData.telephone,
    subjectCategory: 'Borne réparation',
    message: formData.message || formData.description,
    formData
  });
};

// Fonctions utilitaires
export const testAdminInboxService = () => {
  return adminInboxService.testAdminInboxService();
};

export const getAllAdminInboxSubmissions = () => {
  return adminInboxService.getAllLocalSubmissions();
};

export const getAdminInboxFallbacks = () => {
  return adminInboxService.getFallbackSubmissions();
};

export const getAdminInboxStats = () => {
  return adminInboxService.getLocalStats();
};

export const clearAdminInboxData = () => {
  adminInboxService.clearLocalData();
};

// Types pour export
export type { AdminSubmission, AdminInboxResponse };